import type React from 'react';

import { Avatar, Button, Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionStarterInfoTracking } from '#src/app/pages/petitionDetails/shared/hooks';

import { usePetitionStarterPhoto } from '../../hooks/petitionStarterPhoto';

type Petition = PetitionDetailsPageData['petition'];

type Props = {
	user: Petition['user'];
	organization: Petition['organization'];
	createdAt: Petition['createdAt'];
	createdAtLocalized: Petition['createdAtLocalized'];
	petitionPageStarterInHeaderEnabled: boolean;
};

type StarterClusterProps = {
	starterPhotoUrl?: string;
	displayName: string;
};

const StarterCluster: React.FC<StarterClusterProps> = ({ starterPhotoUrl, displayName }) => (
	<span className="flex min-w-0 items-center gap-3">
		{starterPhotoUrl && (
			<span className="shrink-0">
				<Avatar size={28} src={starterPhotoUrl} alt={displayName} />
			</span>
		)}
		<Text variant="body" size="small" fontWeight="bold" truncate className="text-typography-inverse-strong min-w-0">
			{displayName}
		</Text>
	</span>
);

const TitleComponent: React.FC<{ displayName: string }> = ({ displayName }) => (
	<Text variant="heading" size="x-small" fontWeight="bold" className="line-clamp-2 max-w-[90%]">
		{displayName}
	</Text>
);

export const PetitionStarterInHeaderMobile: React.FC<Props> = ({
	user,
	organization,
	createdAt,
	createdAtLocalized,
	petitionPageStarterInHeaderEnabled,
}) => {
	const { photoUrl: starterPhotoUrl } = usePetitionStarterPhoto();
	const locationLabel = user.formattedLocationString;
	const { translate, localizeDate } = useI18n();
	const { trackStarterInfoClick } = usePetitionStarterInfoTracking();

	if (!petitionPageStarterInHeaderEnabled) return null;
	if (organization) return null;
	if (user.anonymous) return null;

	const createdAtLabel = createdAtLocalized || (createdAt ? localizeDate(createdAt, 'date', 'long') : undefined);

	const trigger = (
		<Button
			variant="tertiary"
			size="small"
			data-qa="petition-starter-in-header-mobile-trigger"
			data-testid="petition-starter-in-header-mobile-trigger"
			aria-label={user.displayName}
			className="text-typography-inverse-strong! flex items-center gap-3 bg-transparent! p-0! hover:bg-transparent!"
			onClick={trackStarterInfoClick}
		>
			<StarterCluster starterPhotoUrl={starterPhotoUrl} displayName={user.displayName} />
		</Button>
	);

	const cluster = user.description ? (
		<Modal
			size="small"
			onClose={() => {}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			trigger={trigger}
		>
			<ModalHeader
				title={<TitleComponent displayName={user.displayName} />}
				leadingSlot={starterPhotoUrl ? <Avatar size={56} src={starterPhotoUrl} alt={user.displayName} /> : undefined}
			/>
			<ModalBody>
				<Text variant="body" size="small" className="text-typography-weak">
					{user.description}
				</Text>
			</ModalBody>
		</Modal>
	) : (
		<StarterCluster starterPhotoUrl={starterPhotoUrl} displayName={user.displayName} />
	);

	return (
		<div className="flex flex-col gap-1 pt-2" data-qa="petition-starter-in-header-mobile">
			<div className="flex items-center gap-3">
				{cluster}
				{locationLabel && (
					<>
						<span className="bg-typography-inverse-weak/60 h-3 w-px" aria-hidden />
						<Text variant="body" size="small" className="text-typography-inverse-strong/60">
							{locationLabel}
						</Text>
					</>
				)}
			</div>
			{(createdAtLocalized || createdAt) && (
				<Text variant="body" size="small" className="text-typography-inverse-strong/60 mt-2">
					<Translate
						value="corgi.petition_gamma.petition_starter_in_header.created"
						replacements={{ date: createdAtLabel }}
					/>
				</Text>
			)}
		</div>
	);
};
